import React from 'react';
import { useInView } from 'react-intersection-observer';
import { 
  Users, FileSpreadsheet, Shield, Clock, 
  BarChart, History, Lock, Layout 
} from 'lucide-react';

const features = [
  {
    icon: Users,
    title: 'Multi-User Collaboration',
    description: 'Real-time collaboration with team members worldwide. Comment, suggest changes, and make decisions together.'
  },
  {
    icon: FileSpreadsheet,
    title: 'Smart Report Generation',
    description: 'Generate professional reports in multiple formats with just one click. Customize templates to match your needs.'
  },
  {
    icon: Shield,
    title: 'Advanced Risk Analysis',
    description: 'Integrated risk matrices and intelligent suggestion system for comprehensive hazard evaluation.'
  },
  {
    icon: Clock,
    title: 'Real-time Updates',
    description: 'Instant synchronization across devices ensures all team members work with the latest information.'
  },
  {
    icon: BarChart,
    title: 'Intelligent Node Mapping',
    description: 'AI-assisted node definition and deviation analysis for faster, more accurate studies.'
  },
  {
    icon: History,
    title: 'Complete Audit Trail',
    description: 'Track every change with detailed version history and user activity logs for compliance.'
  },
  {
    icon: Lock,
    title: 'Enterprise Security',
    description: 'Role-based access control and encrypted data storage meeting industry security standards.'
  },
  {
    icon: Layout,
    title: 'Industry Templates',
    description: 'Pre-built templates for various industries, customizable to your specific requirements.'
  }
];

export default function Features() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <section className="py-20 bg-gray-50" id="features">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-black mb-4">
            Powerful Features for Modern Safety Teams
          </h2>
          <p className="text-xl text-black-600 max-w-2xl mx-auto">
            Our comprehensive suite of features makes HAZOP studies more efficient,
            collaborative, and accurate than ever before.
          </p>
        </div>

        <div 
          ref={ref}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index}
                className={`bg-white p-6 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                  inView 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-black-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-black-600">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}